import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  messages,
  seasonWeeks,
  weekRuns,
  type HarvestWeekImportResult,
  type ImportError,
  type MasterData,
} from '@fruitways/shared';
import { api } from '@/api/endpoints';
import { NetworkError } from '@/api/client';
import { Button, ConfirmDialog, Icon, ListRow, SkeletonRows, StatusLabel } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { refreshAfterAdminChange } from '@/hooks/useAdmin';
import { useOnline } from '@/hooks/useOnline';
import { useToast } from '@/hooks/useToast';
import { readSheetFile, saveSheet, type Cell, type ColumnSpec, type SheetValue } from '@/lib/excel';
import { InlineError, InlineNote, adminErrorMessage, t, useAdminData } from './dataShared';
import '@/screens/Capture/capture.css';

type RawRow = { row: number; cells: Record<string, Cell> };

const COLUMNS: ColumnSpec[] = [
  { header: t.import.headers[0], key: 'season', required: true },
  { header: t.import.headers[1], key: 'varietyCode', required: true },
  { header: t.import.headers[2], key: 'weekNumber', required: true },
];
const LIST = '/data/harvest-weeks';

type State =
  | { step: 'empty' }
  | { step: 'checking'; file: string; rows: number }
  | { step: 'unreadable'; file: string; reason: 'notExcel' | 'columns' | 'empty' }
  | { step: 'checked'; file: string; raw: RawRow[]; result: HarvestWeekImportResult };

/**
 * Import variety harvest weeks from an Excel file: get the template, choose a file, check every
 * row with the pack house, then replace every variety harvest week with the file's rows. A file
 * with rows that need fixing replaces nothing.
 */
export function ImportScreen() {
  const nav = useNavigate();
  const toast = useToast();
  const { online } = useOnline();
  const { master, harvestWeeks } = useAdminData();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ step: 'empty' });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = (raw: RawRow[], commit: boolean) =>
    api.admin.harvestWeeks.import({
      commit,
      rows: raw.map((r) => ({
        row: r.row,
        season: r.cells['season'] ?? null,
        varietyCode: r.cells['varietyCode'] ?? null,
        weekNumber: r.cells['weekNumber'] ?? null,
      })),
    });

  /*
   * Every harvest week, not only the current season's: an import replaces all of them, so a file
   * holding one season would wipe the rest on the way back in. A season whose label cannot be
   * resolved keeps its id, which fails the check on re-import rather than vanishing from the file.
   */
  function currentRows(): SheetValue[][] {
    const labelOf = new Map((master?.seasons ?? []).map((s) => [s.id, s.label]));
    return (harvestWeeks ?? [])
      .map((h): SheetValue[] => [
        labelOf.get(h.seasonId) ?? h.seasonId,
        h.varietyCode,
        h.weekNumber,
      ])
      .sort(
        (a, b) =>
          String(a[0]).localeCompare(String(b[0])) ||
          Number(a[2]) - Number(b[2]) ||
          String(a[1]).localeCompare(String(b[1])),
      );
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setState({ step: 'checking', file: file.name, rows: 0 });
    const sheet = await readSheetFile(file, COLUMNS);
    if (!sheet.ok) {
      setState({ step: 'unreadable', file: file.name, reason: sheet.reason });
      return;
    }
    setState({ step: 'checking', file: file.name, rows: sheet.rows.length });
    try {
      const result = await send(sheet.rows, false);
      setState({ step: 'checked', file: file.name, raw: sheet.rows, result });
    } catch (err) {
      setState({ step: 'empty' });
      setError(adminErrorMessage(err));
    }
  }

  async function replace() {
    if (state.step !== 'checked') return;
    setBusy(true);
    setError(null);
    try {
      const result = await send(state.raw, true);
      await refreshAfterAdminChange();
      toast.show(t.import.replaced(result.added));
      nav(LIST, { replace: true });
    } catch (err) {
      setConfirming(false);
      setError(err instanceof NetworkError ? t.import.failed : adminErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setState({ step: 'empty' });
    setError(null);
    if (input.current) input.current.value = '';
  }

  const checked = state.step === 'checked' ? state : null;
  const ready = checked?.result.ready.length ?? 0;
  const errors = checked?.result.errors ?? [];
  const skipped = checked?.result.skipped ?? [];
  const existing = checked?.result.existing ?? 0;
  const missingSeasons = checked?.result.missingSeasons ?? [];
  const canReplace = ready > 0 && errors.length === 0 && missingSeasons.length === 0;
  const raw = new Map(checked?.raw.map((r) => [r.row, r]));

  return (
    <AppFrame
      title={t.import.title}
      subtitle={messages.header.masterData}
      onBack={() => nav(LIST)}
      offlineMessage={messages.banner.offlineData}
    >
      <div className="fw-content">
        <span className="fw-secondary">{t.import.intro}</span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="fw-label">{t.import.step1}</span>
          <div className="fw-rows">
            <ListRow
              leading={<Icon name="document-text-outline" size={24} color="var(--color-primary)" />}
              title={t.import.template}
              secondary={t.import.templateHint}
            />
            <div style={{ padding: 'var(--space-1) var(--space-2)' }}>
              <Button
                variant="ghost"
                icon="download-outline"
                disabled={harvestWeeks === undefined}
                onClick={async () => {
                  const rows = currentRows();
                  await saveSheet(t.import.template, t.import.headers, rows);
                  toast.show(t.import.templateSaved(rows.length));
                }}
              >
                {t.import.getTemplate}
              </Button>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="fw-label">{t.import.step2}</span>
          <input
            ref={input}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => void onFile(e.target.files?.[0])}
            data-testid="import-file"
          />
          {state.step === 'empty' ? (
            <div className="fw-dropzone">
              <Icon name="document-attach-outline" size={40} color="var(--color-brand)" />
              <span className="fw-body-medium">{t.import.noFile}</span>
              <span className="fw-secondary" style={{ maxWidth: '30ch' }}>
                {t.import.fileHint}
              </span>
              <Button variant="secondary" disabled={!online} onClick={() => input.current?.click()}>
                {t.import.chooseFile}
              </Button>
            </div>
          ) : (
            <div className="fw-rows">
              <ListRow
                leading={
                  <Icon name="document-text-outline" size={24} color="var(--color-primary)" />
                }
                title={state.file}
                secondary={
                  state.step === 'checking'
                    ? t.import.checking
                    : state.step === 'unreadable'
                      ? t.import.notImported
                      : t.import.rows(state.raw.length)
                }
                trailing={
                  <button
                    type="button"
                    className="fw-text-button"
                    disabled={state.step === 'checking' || busy}
                    onClick={reset}
                  >
                    {t.import.changeFile}
                  </button>
                }
              />
            </div>
          )}
          {state.step === 'unreadable' && (
            <div className="fw-banner fw-banner--error fw-banner--rounded" role="alert">
              <Icon name="alert-circle" size={20} />
              <span className="fw-banner__text">
                <span className="fw-body-medium" style={{ display: 'block' }}>
                  {state.reason === 'notExcel' ? t.import.notExcelTitle : t.import.unreadableTitle}
                </span>
                {state.reason === 'notExcel'
                  ? t.import.notExcel
                  : state.reason === 'empty'
                    ? t.import.emptyFile
                    : t.import.unreadable}
              </span>
            </div>
          )}
        </div>

        {state.step === 'checking' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="fw-label">{t.import.step3}</span>
            <SkeletonRows count={4} />
          </div>
        )}

        {checked && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span className="fw-label">{t.import.step3}</span>
              <div
                className="fw-panel"
                style={{ background: 'var(--color-surface)', gap: 'var(--space-2)' }}
              >
                {ready > 0 ? (
                  <StatusLabel status="accepted">{t.import.ready(ready)}</StatusLabel>
                ) : (
                  <InlineNote>{t.import.noReady}</InlineNote>
                )}
                {errors.length > 0 && (
                  <StatusLabel status="failed">{t.import.toFix(errors.length)}</StatusLabel>
                )}
                {skipped.length > 0 && <InlineNote>{t.import.willSkip(skipped.length)}</InlineNote>}
                {canReplace && existing > 0 && (
                  <InlineNote>{t.import.replaces(existing)}</InlineNote>
                )}
              </div>
            </div>

            {errors.length > 0 && (
              <Section label={t.import.rowsToFix}>
                {errors.map((e) => (
                  <ListRow
                    key={e.row}
                    leading={<Icon name="alert-circle" size={20} color="var(--color-danger)" />}
                    title={t.import.rowTitle(e.row, e.value)}
                    secondary={fixFor(e, raw.get(e.row), master)}
                  />
                ))}
              </Section>
            )}
            {errors.length > 0 && <InlineNote>{t.import.fixNote}</InlineNote>}
            {errors.length === 0 && missingSeasons.length > 0 && (
              <InlineError>{t.import.missingSeasons(joinOr(missingSeasons))}</InlineError>
            )}

            {skipped.length > 0 && (
              <Section label={t.import.skipped}>
                {skipped.map((s) => (
                  <ListRow
                    key={s.row}
                    leading={
                      <Icon name="information-circle-outline" size={20} color="var(--text-muted)" />
                    }
                    title={t.import.rowTitle(s.row, t.import.readyRow(s.varietyCode, s.weekNumber))}
                    secondary={t.import.duplicateRow(s.sameAsRow)}
                  />
                ))}
              </Section>
            )}

            {ready > 0 && (
              <Section label={t.import.readyToImport}>
                {checked.result.ready.map((r) => (
                  <ListRow
                    key={r.row}
                    title={t.import.readyRow(r.varietyCode, r.weekNumber)}
                    meta={t.import.rowLabel(r.row)}
                  />
                ))}
              </Section>
            )}
          </>
        )}
        {error && <InlineError>{error}</InlineError>}
      </div>
      <div className="fw-actions fw-actions--column">
        <Button
          disabled={!online || !canReplace}
          loading={state.step === 'checking'}
          onClick={() => setConfirming(true)}
        >
          {t.import.replaceN(canReplace ? ready : 0)}
        </Button>
        <Button variant="ghost" onClick={() => nav(LIST)}>
          {messages.common.cancel}
        </Button>
      </div>
      <ConfirmDialog
        open={confirming}
        title={t.import.confirmTitle}
        body={t.import.confirmBody(existing, ready)}
        note={t.import.confirmNote}
        confirmLabel={t.import.confirm}
        cancelLabel={messages.common.cancel}
        busy={busy}
        onConfirm={() => void replace()}
        onCancel={() => setConfirming(false)}
      />
    </AppFrame>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <span className="fw-label">{label}</span>
      <div className="fw-rows">{children}</div>
    </div>
  );
}

/** `2025/2026, 2026/2027 or 2027/2028` */
function joinOr(labels: readonly string[]): string {
  const last = labels[labels.length - 1] ?? '';
  return labels.length > 1 ? t.import.joinOr(labels.slice(0, -1).join(', '), last) : last;
}

/** Copy that says how to fix a row. */
function fixFor(e: ImportError, raw: RawRow | undefined, master: MasterData | undefined): string {
  const r = t.import.reason;
  switch (e.reason) {
    case 'missing_value':
      return r.missing_value;
    case 'unknown_variety':
      return r.unknown_variety(e.value);
    case 'invalid_week':
      return r.invalid_week;
    case 'unknown_season':
      return r.unknown_season(joinOr((master?.seasons ?? []).map((s) => s.label)));
    case 'week_not_in_season': {
      const season = master?.seasons.find((s) => s.id === e.seasonId);
      const valid = season
        ? weekRuns(seasonWeeks(season.startDate, season.endDate).map((w) => w.weekNumber))
        : '';
      return r.week_not_in_season(
        String(raw?.cells['weekNumber'] ?? ''),
        season?.label ?? '',
        valid,
      );
    }
  }
}
